import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import DeleteModal from 'modules/timeoff/components/TimeoffAllowances/UpsertAllowance/TenurePeriods/DeleteModal'
import FormModal from 'modules/timeoff/components/TimeoffAllowances/UpsertAllowance/TenurePeriods/FormModal'
import PeriodItem from 'modules/timeoff/components/TimeoffAllowances/UpsertAllowance/TenurePeriods/PeriodItem'
import type AllowanceForm from 'modules/timeoff/lib/AllowanceForm'
import PeriodForm from 'modules/timeoff/lib/PeriodForm'
import TenurePeriod from 'modules/timeoff/lib/TenurePeriod'
import type { ITenurePeriod as PeriodAttributes } from 'modules/timeoff/lib/TenurePeriod'

interface HeaderProps {
  baseAllowance: number
  allowanceMaxBalanceCap: number | null
  isHourly: boolean
}

const ListHeader = ({ baseAllowance, allowanceMaxBalanceCap, isHourly }: HeaderProps) => {
  const title = isHourly
    ? i18n.t('time_off.allowances.page.labels.base_allowance_hours')
    : i18n.t('time_off.allowances.page.labels.base_allowance_days')

  const subtitle = isHourly
    ? i18n.tp('time_off.allowances.page.tenure_periods.item.total_adjustment_in_hours', {
        count: baseAllowance,
      })
    : i18n.tp('time_off.allowances.page.tenure_periods.item.total_adjustment_in_days', {
        count: baseAllowance,
      })

  const maxBalanceCapSubtitle = isHourly
    ? i18n.t('time_off.allowances.page.tenure_periods.item.max_balance_hours')
    : i18n.t('time_off.allowances.page.tenure_periods.item.max_balance_days')

  return (
    <Box
      paddingX="s24"
      paddingY="s16"
      border={{
        top: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
    >
      <Text.Header2>{title}</Text.Header2>
      <Box flexGrow flexDirection="row" justifyContent="spaceBetween">
        <Text.Normal color="grey600">{subtitle}</Text.Normal>
        <Text.Normal color="grey600">{baseAllowance}</Text.Normal>
      </Box>
      {allowanceMaxBalanceCap && (
        <Box flexGrow flexDirection="row" justifyContent="spaceBetween">
          <Text.Normal color="grey600">{maxBalanceCapSubtitle}</Text.Normal>
          <Text.Normal color="grey600">{allowanceMaxBalanceCap}</Text.Normal>
        </Box>
      )}
    </Box>
  )
}

interface Props {
  allowanceForm: AllowanceForm
  allowanceMaxBalanceCap: number | null
  isHourly: boolean
  hasProratedTenures: boolean
}

const PeriodList = ({
  allowanceForm,
  allowanceMaxBalanceCap,
  isHourly,
  hasProratedTenures,
}: Props) => {
  const tenurePeriods = allowanceForm.get('tenure_periods').value
  const baseAllowance = parseFloat(allowanceForm.get('holiday_allowance_in_cents').value) || 0
  const tenurePeriodForm = (periodData: PeriodAttributes) => new PeriodForm(periodData)

  const [periodToEdit, setPeriodToEdit] = React.useState<PeriodAttributes | null | undefined>(null)

  const [periodToDelete, setPeriodToDelete] = React.useState<PeriodAttributes | null | undefined>(
    null
  )

  const onDelete = (period: PeriodAttributes) => setPeriodToDelete(period)

  const onEdit = (period: PeriodAttributes) => {
    setPeriodToEdit(period)
  }

  const closeEditModal = () => {
    setPeriodToEdit(null)
  }

  const onSave = (periodEdited: PeriodAttributes) => {
    const otherPeriods = tenurePeriods.filter(
      (p: { id: string | number }) => p.id !== periodEdited.id
    )
    const editedPeriods = [periodEdited, ...otherPeriods]

    allowanceForm.setValues({
      tenure_periods: editedPeriods,
    })

    closeEditModal()
  }

  const closeDeleteModal = () => setPeriodToDelete(null)

  const onConfirmDelete = () => {
    if (!periodToDelete) return

    periodToDelete._destroy = true
    closeDeleteModal()
  }

  const filteredPeriods = tenurePeriods.filter((period: PeriodAttributes) => !period._destroy)

  const sortedPeriods = TenurePeriod.sortByPeriodLength(filteredPeriods)

  const ptoTenuresEnabled = hasProratedTenures && allowanceForm.isTimeWorked

  return (
    <Box background="grey000White">
      <ListHeader
        allowanceMaxBalanceCap={allowanceMaxBalanceCap}
        baseAllowance={baseAllowance}
        isHourly={isHourly}
      />
      {sortedPeriods.map((payload, i) => {
        return (
          <PeriodItem
            key={i}
            payload={payload}
            onEdit={onEdit}
            onDelete={onDelete}
            baseAllowance={baseAllowance}
            isHourly={isHourly}
          />
        )
      })}
      {!!periodToEdit && (
        <FormModal
          periodForm={tenurePeriodForm(periodToEdit)}
          closeModal={closeEditModal}
          onSave={onSave}
          baseAllowance={baseAllowance}
          ptoTenuresEnabled={ptoTenuresEnabled}
          isHourly={isHourly}
        />
      )}
      {!!periodToDelete && (
        <DeleteModal closeModal={closeDeleteModal} onConfirm={onConfirmDelete} />
      )}
    </Box>
  )
}

export default observer(PeriodList)
